import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import ResetPasswordModal from '../Authorisation/ResetPasswordModal';

interface ProfilePicture {
  file_name: string; key: string; cdn_url: string; dev_url: string;
  size: number; mime_type: string; etag: string;
  uploaded_at: { $date: { $numberLong: string } };
}

interface Teacher {
  teacher_id: string; name: string; dateOfBirth: string | null;
  gender: string | null; aadharNumber: string | null; category: string | null;
  mobileNumber: string | null; email: string; district: string | null;
  state: string | null; pinCode: string | null; maritalStatus: string | null;
  qualification: string | null; dateOfJoining: string | null; employeeId: string | null;
  experience: string | null; bloodGroup: string | null; whatsappNumber: string | null;
  wardNo: string | null; villageTown: string | null; tehsil: string | null;
  spouseName: string | null; spouseContact: string | null;
  emergencyContactName: string | null; emergencyContactNumber: string | null;
  profilePicture: ProfilePicture | null; status: string;
  created_at: string | null; created_by: string | null; is_registered: boolean;
}

interface CredentialResponse {
  email: string; temporary_password: string;
  institute_name?: string; branch_name?: string;
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

const labelStyle: React.CSSProperties = {
  margin: '0 0 8px 0', fontSize: '0.68rem', color: '#9ca3af',
  fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px'
};

const TeacherDetails: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const [teacher, setTeacher]                             = useState<Teacher | null>(null);
  const [loading, setLoading]                             = useState(false);
  const [error, setError]                                 = useState<string | null>(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [generatingCredentials, setGeneratingCredentials] = useState(false);
  const [credentials, setCredentials]                     = useState<CredentialResponse | null>(null);
  const [emailSent, setEmailSent]                         = useState(false);
  const [showResetModal, setShowResetModal]               = useState(false);

  const toast = React.useRef<Toast>(null);

  useEffect(() => { if (teacherId) fetchTeacherDetails(); }, [teacherId]);

  const fetchTeacherDetails = async () => {
    if (!teacherId) return;
    try {
      setLoading(true); setError(null);
      const response = await apiClient.get(`/v1/teachers/get-teacher/${teacherId}`);
      if (response.data.success) setTeacher(response.data.data);
      else setError('Failed to load teacher details');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load teacher details');
    } finally { setLoading(false); }
  };

  const handleGenerateCredentials = async () => {
    if (!teacherId || !teacher) return;
    if (!teacher.email) {
      toast.current?.show({ severity: 'error', summary: 'Missing Email', detail: 'Teacher must have an email address to generate credentials', life: 4000 });
      return;
    }
    if (!teacher.name || !teacher.dateOfBirth) {
      toast.current?.show({ severity: 'error', summary: 'Missing Information', detail: 'Teacher must have name and date of birth', life: 4000 });
      return;
    }
    try {
      setGeneratingCredentials(true);
      const response = await apiClient.post(`/v1/teachers/${teacherId}/generate-credentials`);
      if (response.data.success) {
        const d = response.data.data;
        setCredentials({ email: d.email, temporary_password: d.temporary_password, institute_name: d.institute_name, branch_name: d.branch_name });
        setEmailSent(response.data.email_sent === true);
        setShowCredentialsDialog(true);
        setTeacher(prev => prev ? { ...prev, is_registered: true } : null);
        toast.current?.show({
          severity: response.data.email_sent ? 'success' : 'warn',
          summary: response.data.email_sent ? 'Success' : 'Credentials Generated',
          detail: response.data.email_sent ? 'Credentials generated and welcome email sent!' : 'Credentials created but email could not be sent.',
          life: response.data.email_sent ? 4000 : 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to generate credentials', life: 4000 });
    } finally { setGeneratingCredentials(false); }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.current?.show({ severity: 'info', summary: 'Copied', detail: `${label} copied to clipboard`, life: 2000 });
  };

  const copyAllCredentials = () => {
    if (!credentials) return;
    const text = `Teacher Portal Access\n========================\nName: ${teacher?.name || 'N/A'}\nEmail/Username: ${credentials.email}\nTemporary Password: ${credentials.temporary_password}\nInstitute: ${credentials.institute_name || 'N/A'}\nBranch: ${credentials.branch_name || 'N/A'}\n\n⚠️ Please change your password immediately after first login.`.trim();
    navigator.clipboard.writeText(text);
    toast.current?.show({ severity: 'success', summary: 'Copied', detail: 'All credentials copied to clipboard', life: 2000 });
  };

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
      <div style={{ textAlign: 'center' }}>
        <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.95rem', fontWeight: '500' }}>Loading teacher details...</p>
      </div>
    </div>
  );

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error || !teacher) return (
    <div style={{ padding: '24px' }}>
      <Message severity="error" text={error || 'Teacher not found'} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />
      <button onClick={() => navigate('/teachers')} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}>
        <i className="pi pi-arrow-left" /> Back to Teachers
      </button>
    </div>
  );

  // ── Main ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0' }}>
      <Toast ref={toast} />

      <ResetPasswordModal
        visible={showResetModal}
        onHide={() => setShowResetModal(false)}
        onSuccess={() => {
          setShowResetModal(false);
          toast.current?.show({ severity: 'success', summary: 'Password Reset', detail: `Password for ${teacher.name} has been reset successfully`, life: 4000 });
        }}
        profileId={teacher.teacher_id}
        role="teacher"
        name={teacher.name}
      />

      {/* ── Hero Banner ───────────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
        borderRadius: '20px', padding: '32px', marginBottom: '24px',
        color: C.white, position: 'relative', overflow: 'hidden',
        boxShadow: '2px 0 12px rgba(14,34,93,0.3)'
      }}>
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: '140px', height: '140px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <button
            onClick={() => navigate('/teachers')}
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: C.white, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '20px', transition: 'all 0.2s ease' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <i className="pi pi-arrow-left" /> Back to Teachers
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>
            {/* Left — avatar + name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1, minWidth: '250px' }}>
              <Avatar
                image={teacher.profilePicture?.cdn_url || undefined}
                icon={!teacher.profilePicture ? 'pi pi-user' : undefined}
                size="xlarge" shape="circle"
                style={{ width: '100px', height: '100px', backgroundColor: teacher.profilePicture ? 'transparent' : C.goldDim, color: C.gold, border: `4px solid ${C.goldBorder}`, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
              />
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
                  <i className="pi pi-user" style={{ fontSize: '0.7rem' }} />
                  Teacher Profile
                </div>
                <h1 style={{ margin: '0 0 6px 0', fontSize: '1.75rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>{teacher.name}</h1>
                <p style={{ margin: '0 0 14px 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>Employee ID: {teacher.employeeId || 'N/A'}</p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {teacher.mobileNumber && (
                    <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '8px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="pi pi-phone" style={{ fontSize: '0.8rem', color: C.gold }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.white }}>{teacher.mobileNumber}</span>
                    </div>
                  )}
                  {teacher.email && (
                    <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '8px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="pi pi-envelope" style={{ fontSize: '0.8rem', color: C.gold }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.white }}>{teacher.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right — badges + dates */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.5px', ...(teacher.status === 'active' ? { background: C.gold, color: C.navy } : { background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)' }) }}>
                  {teacher.status.toUpperCase()}
                </span>
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.5px', ...(teacher.is_registered ? { background: C.gold, color: C.navy } : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.2)' }) }}>
                  {teacher.is_registered ? 'REGISTERED' : 'NOT REGISTERED'}
                </span>
              </div>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)' }}>DOB: {formatDate(teacher.dateOfBirth)}</p>
              <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>Joined {formatDate(teacher.dateOfJoining)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Grid ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '24px' }}>

        {/* ── Left Column ──────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Not-registered warning */}
          {!teacher.is_registered && (
            <div style={{ background: 'rgba(251,178,31,0.08)', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `2px solid ${C.goldBorder}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem', color: '#92580a' }} />
                  <div>
                    <p style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: '700', color: '#92580a' }}>Account Not Registered</p>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#92580a' }}>Generate login credentials to allow teacher portal access. A welcome email will be sent automatically.</p>
                  </div>
                </div>
                <button
                  onClick={handleGenerateCredentials}
                  disabled={generatingCredentials}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: C.gold, border: 'none', borderRadius: '10px', color: C.navy, fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(251,178,31,0.35)', transition: 'all 0.2s ease', flexShrink: 0 }}
                  onMouseEnter={e => { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <i className={generatingCredentials ? 'pi pi-spin pi-spinner' : 'pi pi-key'} style={{ fontSize: '0.9rem' }} />
                  {generatingCredentials ? 'Generating...' : 'Generate Credentials'}
                </button>
              </div>
            </div>
          )}

          <InfoSection title="Personal Information" icon="pi-user" fields={[
            { label: 'Gender', value: teacher.gender ? teacher.gender.charAt(0).toUpperCase() + teacher.gender.slice(1) : '-' },
            { label: 'Blood Group', value: teacher.bloodGroup || '-' },
            { label: 'Category', value: teacher.category || '-' },
            { label: 'Aadhaar Number', value: teacher.aadharNumber || '-' },
            { label: 'Marital Status', value: teacher.maritalStatus ? teacher.maritalStatus.charAt(0).toUpperCase() + teacher.maritalStatus.slice(1) : '-' },
          ]} />

          <InfoSection title="Professional Information" icon="pi-briefcase" fields={[
            { label: 'Qualification', value: teacher.qualification || '-' },
            { label: 'Experience', value: teacher.experience || '-' },
            { label: 'Employee ID', value: teacher.employeeId || '-' },
            { label: 'Date of Joining', value: formatDate(teacher.dateOfJoining) },
          ]} />

          <InfoSection title="Contact Information" icon="pi-phone" fields={[
            { label: 'Mobile Number', value: teacher.mobileNumber || '-' },
            { label: 'WhatsApp Number', value: teacher.whatsappNumber || '-' },
            { label: 'Email', value: teacher.email || '-' },
          ]} />

          {/* Address */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <SectionHeader title="Address" subtitle="Residential information" icon="pi-map-marker" />
            <div style={{ background: C.bg, padding: '16px', borderRadius: '10px', marginBottom: '16px', border: `1px solid ${C.border}` }}>
              <p style={labelStyle}>Full Address</p>
              <p style={{ margin: 0, fontSize: '0.95rem', color: C.body, lineHeight: '1.5', fontWeight: '500' }}>
                {[teacher.wardNo && `Ward ${teacher.wardNo}`, teacher.villageTown, teacher.tehsil, teacher.district, teacher.state].filter(Boolean).join(', ') || '-'}
              </p>
              {teacher.pinCode && <p style={{ margin: '6px 0 0 0', fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Pincode: {teacher.pinCode}</p>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <InfoFieldItem label="Ward No" value={teacher.wardNo || '-'} />
              <InfoFieldItem label="Village/Town" value={teacher.villageTown || '-'} />
              <InfoFieldItem label="Tehsil" value={teacher.tehsil || '-'} />
            </div>
          </div>

          {/* Family / Emergency */}
          {(teacher.spouseName || teacher.spouseContact || teacher.emergencyContactName || teacher.emergencyContactNumber) && (
            <div style={{ background: C.white, borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <SectionHeader title="Family & Emergency Contact" subtitle="Personal contacts" icon="pi-users" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {(teacher.spouseName || teacher.spouseContact) && (
                  <div style={{ background: C.bg, padding: '14px', borderRadius: '10px', border: `1px solid ${C.border}` }}>
                    <p style={labelStyle}>Spouse Details</p>
                    <InfoFieldItem label="Name" value={teacher.spouseName || '-'} compact />
                    <InfoFieldItem label="Contact" value={teacher.spouseContact || '-'} compact />
                  </div>
                )}
                {(teacher.emergencyContactName || teacher.emergencyContactNumber) && (
                  <div style={{ background: C.bg, padding: '14px', borderRadius: '10px', border: `1px solid ${C.border}` }}>
                    <p style={labelStyle}>Emergency Contact</p>
                    <InfoFieldItem label="Name" value={teacher.emergencyContactName || '-'} compact />
                    <InfoFieldItem label="Number" value={teacher.emergencyContactNumber || '-'} compact />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Right Column ──────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Quick Info */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-id-card" style={{ color: C.gold, fontSize: '1rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>Quick Info</h3>
            </div>

            {/* Avatar card — navy gradient */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '12px', marginBottom: '16px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: C.goldDim, borderRadius: '50%', filter: 'blur(20px)', pointerEvents: 'none' }} />
              <Avatar
                image={teacher.profilePicture?.cdn_url || undefined}
                icon={!teacher.profilePicture ? 'pi pi-user' : undefined}
                size="xlarge" shape="circle"
                style={{ width: '80px', height: '80px', backgroundColor: teacher.profilePicture ? 'transparent' : C.goldDim, color: C.gold, marginBottom: '12px', border: `3px solid ${C.goldBorder}` }}
              />
              <p style={{ margin: '0 0 4px 0', fontSize: '1.1rem', fontWeight: '700', color: C.white, textAlign: 'center', position: 'relative', zIndex: 1 }}>{teacher.name}</p>
              <p style={{ margin: '0 0 12px 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', position: 'relative', zIndex: 1 }}>{teacher.qualification || 'Teacher'}</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', ...(teacher.status === 'active' ? { background: C.gold, color: C.navy } : { background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)' }) }}>
                  {teacher.status.toUpperCase()}
                </span>
                <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', ...(teacher.is_registered ? { background: C.gold, color: C.navy } : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.2)' }) }}>
                  {teacher.is_registered ? 'REGISTERED' : 'NOT REGISTERED'}
                </span>
              </div>
            </div>

            <QuickInfoRow label="Teacher ID" value={teacher.teacher_id} mono />
            <QuickInfoRow label="Employee ID" value={teacher.employeeId || '-'} />
            <QuickInfoRow label="Date of Birth" value={formatDate(teacher.dateOfBirth)} />
            {teacher.gender && <QuickInfoRow label="Gender" value={teacher.gender.charAt(0).toUpperCase() + teacher.gender.slice(1)} />}
            {teacher.category && <QuickInfoRow label="Category" value={teacher.category} />}
          </div>

          {/* Password Management */}
          {teacher.is_registered && (
            <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-lock" style={{ color: C.gold, fontSize: '0.9rem' }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Password Management</p>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: C.muted }}>Operator controls</p>
                </div>
              </div>
              <p style={{ margin: '0 0 14px 0', fontSize: '0.78rem', color: C.sub, lineHeight: '1.5' }}>
                Reset this teacher's login password if they've forgotten it or need access restored.
              </p>
              <button
                onClick={() => setShowResetModal(true)}
                style={{ width: '100%', padding: '11px 16px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(14,34,93,0.2)' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.2)'; }}
              >
                <i className="pi pi-refresh" style={{ fontSize: '0.82rem' }} />
                Reset Password
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Action Buttons Bar ──────────────────────────────────────────────── */}
      <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}`, display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {/* Primary — gold */}
        <button
          onClick={() => navigate(`/teachers/edit/${teacherId}`)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: C.gold, border: 'none', borderRadius: '10px', color: C.navy, fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(251,178,31,0.3)', transition: 'all 0.2s ease' }}
          onMouseEnter={e => { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <i className="pi pi-pencil" style={{ fontSize: '0.85rem' }} /> Edit Teacher
        </button>
        {/* Secondary — navy outlined */}
        {[
          { label: 'Assign Batches',   icon: 'pi-users',    onClick: () => {} },
          { label: 'View Attendance',  icon: 'pi-calendar', onClick: () => {} },
        ].map(action => (
          <button
            key={action.label}
            onClick={action.onClick}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '10px', color: C.navy, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
            onMouseEnter={e => { e.currentTarget.style.background = C.navy; (e.currentTarget as HTMLButtonElement).style.color = C.white; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = C.navy; }}
          >
            <i className={`pi ${action.icon}`} style={{ fontSize: '0.85rem' }} /> {action.label}
          </button>
        ))}
        {!teacher.is_registered && (
          <button
            onClick={handleGenerateCredentials}
            disabled={generatingCredentials}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: 'transparent', border: `1.5px solid #92580a`, borderRadius: '10px', color: '#92580a', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
          >
            <i className="pi pi-key" style={{ fontSize: '0.85rem' }} /> Generate Credentials
          </button>
        )}
        {teacher.is_registered && (
          <button
            onClick={() => setShowResetModal(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '10px', color: C.navy, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
            onMouseEnter={e => { e.currentTarget.style.background = C.navy; (e.currentTarget as HTMLButtonElement).style.color = C.white; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = C.navy; }}
          >
            <i className="pi pi-lock" style={{ fontSize: '0.85rem' }} /> Reset Password
          </button>
        )}
      </div>

      {/* ── Credentials Dialog ───────────────────────────────────────────────── */}
      <Dialog
        visible={showCredentialsDialog}
        style={{ width: '560px' }}
        onHide={() => { setShowCredentialsDialog(false); setCredentials(null); setEmailSent(false); }}
        pt={{
          root: { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
          header: { style: { display: 'none' } },
          content: { style: { padding: '0' } },
          footer: { style: { display: 'none' } }
        }}
      >
        {credentials && (
          <div>
            {/* Dialog header — navy gradient */}
            <div style={{ background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)`, padding: '24px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '100px', height: '100px', background: C.goldDim, borderRadius: '50%', filter: 'blur(20px)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '44px', height: '44px', background: C.gold, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="pi pi-check-circle" style={{ fontSize: '1.3rem', color: C.navy }} />
                </div>
                <div>
                  <h3 style={{ margin: '0 0 3px 0', fontSize: '1.2rem', fontWeight: '700', color: C.white }}>Credentials Generated</h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{emailSent ? 'Welcome email sent successfully' : 'Please share credentials manually'}</p>
                </div>
              </div>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Email status */}
              <div style={{ background: emailSent ? '#f0fdf4' : 'rgba(251,178,31,0.08)', padding: '14px 16px', borderRadius: '12px', marginBottom: '20px', border: emailSent ? '1px solid #86efac' : `1px solid ${C.goldBorder}`, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <i className={emailSent ? 'pi pi-check-circle' : 'pi pi-info-circle'} style={{ fontSize: '1.1rem', color: emailSent ? '#15803d' : '#92580a', marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '0.88rem', fontWeight: '700', color: emailSent ? '#15803d' : '#92580a' }}>
                    {emailSent ? 'Welcome Email Sent Successfully' : 'Manual Sharing Required'}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: '1.5', color: emailSent ? '#166534' : '#78350f' }}>
                    {emailSent ? `A welcome email with login credentials has been sent to ${credentials.email}.` : 'The welcome email could not be sent. Please share credentials manually.'}
                  </p>
                </div>
              </div>

              {/* Teacher context */}
              {(credentials.institute_name || credentials.branch_name) && (
                <div style={{ background: C.bg, padding: '14px 16px', borderRadius: '12px', marginBottom: '20px', border: `1px solid ${C.border}` }}>
                  <p style={labelStyle}>Teacher Details</p>
                  {credentials.institute_name && <p style={{ margin: '0 0 6px 0', fontSize: '0.82rem', color: C.body }}><span style={{ color: C.muted, fontWeight: '600' }}>Institute: </span>{credentials.institute_name}</p>}
                  {credentials.branch_name && <p style={{ margin: 0, fontSize: '0.82rem', color: C.body }}><span style={{ color: C.muted, fontWeight: '600' }}>Branch: </span>{credentials.branch_name}</p>}
                </div>
              )}

              <CredentialRow label="Email / Username" value={credentials.email} onCopy={() => copyToClipboard(credentials.email, 'Email')} />
              <CredentialRow label="Temporary Password" value={credentials.temporary_password} onCopy={() => copyToClipboard(credentials.temporary_password, 'Password')} highlight />

              {/* Security notice */}
              <div style={{ background: 'rgba(251,178,31,0.08)', border: `1px solid ${C.goldBorder}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <i className="pi pi-shield" style={{ fontSize: '1rem', color: '#92580a', marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: '#92580a' }}>Important Security Notice</p>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#78350f', lineHeight: '1.5' }}>Teacher must change this temporary password immediately after first login.</p>
                </div>
              </div>

              {/* Footer actions */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {emailSent
                    ? <><i className="pi pi-check-circle" style={{ color: '#15803d', fontSize: '0.9rem' }} /><span style={{ fontSize: '0.82rem', color: '#15803d', fontWeight: '600' }}>Email sent</span></>
                    : <><i className="pi pi-exclamation-triangle" style={{ color: '#92580a', fontSize: '0.9rem' }} /><span style={{ fontSize: '0.82rem', color: '#92580a', fontWeight: '600' }}>Email not sent</span></>}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={copyAllCredentials}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '10px', color: C.navy, fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s ease' }}
                    onMouseEnter={e => e.currentTarget.style.background = C.bg}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="pi pi-copy" /> Copy All
                  </button>
                  <button
                    onClick={() => { setShowCredentialsDialog(false); setCredentials(null); setEmailSent(false); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.2)', transition: 'all 0.2s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.2)'; }}
                  >
                    <i className="pi pi-times" /> Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ title: string; subtitle: string; icon: string }> = ({ title, subtitle, icon }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
    <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <i className={`pi ${icon}`} style={{ color: '#fbb21f', fontSize: '1.1rem' }} />
    </div>
    <div>
      <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#0e225d' }}>{title}</h2>
      <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>{subtitle}</p>
    </div>
  </div>
);

interface InfoSectionProps {
  title: string; icon: string;
  fields: Array<{ label: string; value: string | null }>;
}
const InfoSection: React.FC<InfoSectionProps> = ({ title, icon, fields }) => (
  <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB' }}>
    <SectionHeader title={title} subtitle={`Teacher ${title.toLowerCase()}`} icon={icon} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
      {fields.map((f, i) => <InfoFieldItem key={i} label={f.label} value={f.value} />)}
    </div>
  </div>
);

const InfoFieldItem: React.FC<{ label: string; value: string | null; compact?: boolean }> = ({ label, value, compact }) => (
  <div style={{ marginBottom: compact ? '8px' : '0' }}>
    <p style={{ margin: '0 0 5px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</p>
    <p style={{ margin: 0, fontSize: compact ? '0.8rem' : '0.9rem', fontWeight: '600', color: '#1f2937' }}>{value || '-'}</p>
  </div>
);

const QuickInfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div style={{ marginBottom: '12px' }}>
    <p style={{ margin: '0 0 5px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</p>
    <p style={{ margin: 0, fontSize: mono ? '0.8rem' : '0.85rem', fontWeight: '600', color: mono ? '#0e225d' : '#1f2937', fontFamily: mono ? 'monospace' : undefined, background: mono ? '#F5F7FA' : undefined, padding: mono ? '8px 10px' : undefined, borderRadius: mono ? '8px' : undefined, border: mono ? '1px solid #E5E7EB' : undefined, wordBreak: 'break-all' }}>{value}</p>
  </div>
);

const CredentialRow: React.FC<{ label: string; value: string; onCopy: () => void; highlight?: boolean }> = ({ label, value, onCopy, highlight }) => (
  <div style={{ marginBottom: '14px' }}>
    <p style={{ margin: '0 0 7px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</p>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: highlight ? 'rgba(251,178,31,0.08)' : '#F5F7FA', padding: '12px 14px', borderRadius: '10px', border: highlight ? '1px solid rgba(251,178,31,0.35)' : '1px solid #E5E7EB' }}>
      <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: highlight ? '700' : '600', color: highlight ? '#92580a' : '#0e225d', flex: 1, fontFamily: 'monospace', letterSpacing: highlight ? '1px' : '0.3px' }}>{value}</p>
      <button onClick={onCopy} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px', color: highlight ? '#92580a' : '#0e225d', transition: 'background 0.2s' }} title="Copy"
        onMouseEnter={e => e.currentTarget.style.background = highlight ? 'rgba(251,178,31,0.15)' : 'rgba(14,34,93,0.08)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <i className="pi pi-copy" style={{ fontSize: '0.9rem' }} />
      </button>
    </div>
  </div>
);

export default TeacherDetails;


// import React, { useState, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { ProgressSpinner } from 'primereact/progressspinner';
// import { Message } from 'primereact/message';
// import { Avatar } from 'primereact/avatar';
// import { Tag } from 'primereact/tag';
// import { Button } from 'primereact/button';
// import { Dialog } from 'primereact/dialog';
// import { Toast } from 'primereact/toast';
// import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
// import ResetPasswordModal from '../Authorisation/ResetPasswordModal';

// interface ProfilePicture {
//   file_name: string;
//   key: string;
//   cdn_url: string;
//   dev_url: string;
//   size: number;
//   mime_type: string;
//   etag: string;
//   uploaded_at: { $date: { $numberLong: string } };
// }

// interface Teacher {
//   teacher_id: string;
//   name: string;
//   dateOfBirth: string | null;
//   gender: string | null;
//   aadharNumber: string | null;
//   category: string | null;
//   mobileNumber: string | null;
//   email: string;
//   district: string | null;
//   state: string | null;
//   pinCode: string | null;
//   maritalStatus: string | null;
//   qualification: string | null;
//   dateOfJoining: string | null;
//   employeeId: string | null;
//   experience: string | null;
//   bloodGroup: string | null;
//   whatsappNumber: string | null;
//   wardNo: string | null;
//   villageTown: string | null;
//   tehsil: string | null;
//   spouseName: string | null;
//   spouseContact: string | null;
//   emergencyContactName: string | null;
//   emergencyContactNumber: string | null;
//   profilePicture: ProfilePicture | null;
//   status: string;
//   created_at: string | null;
//   created_by: string | null;
//   is_registered: boolean;
// }

// interface CredentialResponse {
//   email: string;
//   temporary_password: string;
//   institute_name?: string;
//   branch_name?: string;
// }

// const TeacherDetails: React.FC = () => {
//   const { teacherId } = useParams<{ teacherId: string }>();
//   const navigate = useNavigate();
//   const [teacher, setTeacher]                             = useState<Teacher | null>(null);
//   const [loading, setLoading]                             = useState(false);
//   const [error, setError]                                 = useState<string | null>(null);
//   const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
//   const [generatingCredentials, setGeneratingCredentials] = useState(false);
//   const [credentials, setCredentials]                     = useState<CredentialResponse | null>(null);
//   const [emailSent, setEmailSent]                         = useState(false);

//   // ── Reset Password modal state ──────────────────────────────────────────────
//   const [showResetModal, setShowResetModal] = useState(false);

//   const toast = React.useRef<Toast>(null);

//   useEffect(() => {
//     if (teacherId) fetchTeacherDetails();
//   }, [teacherId]);

//   const fetchTeacherDetails = async () => {
//     if (!teacherId) return;
//     try {
//       setLoading(true);
//       setError(null);
//       const response = await apiClient.get(`/v1/teachers/get-teacher/${teacherId}`);
//       if (response.data.success) {
//         setTeacher(response.data.data);
//       } else {
//         setError('Failed to load teacher details');
//       }
//     } catch (err: any) {
//       setError(err.response?.data?.error || 'Failed to load teacher details');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleGenerateCredentials = async () => {
//     if (!teacherId || !teacher) return;
//     if (!teacher.email) {
//       toast.current?.show({ severity: 'error', summary: 'Missing Email', detail: 'Teacher must have an email address to generate credentials', life: 4000 });
//       return;
//     }
//     if (!teacher.name || !teacher.dateOfBirth) {
//       toast.current?.show({ severity: 'error', summary: 'Missing Information', detail: 'Teacher must have name and date of birth', life: 4000 });
//       return;
//     }
//     try {
//       setGeneratingCredentials(true);
//       const response = await apiClient.post(`/v1/teachers/${teacherId}/generate-credentials`);
//       if (response.data.success) {
//         const credData = response.data.data;
//         setCredentials({ email: credData.email, temporary_password: credData.temporary_password, institute_name: credData.institute_name, branch_name: credData.branch_name });
//         setEmailSent(response.data.email_sent === true);
//         setShowCredentialsDialog(true);
//         setTeacher(prev => prev ? { ...prev, is_registered: true } : null);
//         toast.current?.show({
//           severity: response.data.email_sent ? 'success' : 'warn',
//           summary: response.data.email_sent ? 'Success' : 'Credentials Generated',
//           detail: response.data.email_sent
//             ? 'Teacher credentials generated and welcome email sent successfully!'
//             : 'Credentials created successfully, but email could not be sent.',
//           life: response.data.email_sent ? 4000 : 5000,
//         });
//       }
//     } catch (err: any) {
//       toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to generate credentials', life: 4000 });
//     } finally {
//       setGeneratingCredentials(false);
//     }
//   };

//   const copyToClipboard = (text: string, label: string) => {
//     navigator.clipboard.writeText(text);
//     toast.current?.show({ severity: 'info', summary: 'Copied', detail: `${label} copied to clipboard`, life: 2000 });
//   };

//   const copyAllCredentials = () => {
//     if (!credentials) return;
//     const text = `HCI Teacher Portal Access\n========================\nName: ${teacher?.name || 'N/A'}\nEmail/Username: ${credentials.email}\nTemporary Password: ${credentials.temporary_password}\nInstitute: ${credentials.institute_name || 'N/A'}\nBranch: ${credentials.branch_name || 'N/A'}\n\n⚠️ IMPORTANT: Please change your password immediately after first login.`.trim();
//     navigator.clipboard.writeText(text);
//     toast.current?.show({ severity: 'success', summary: 'Copied', detail: 'All credentials copied to clipboard', life: 2000 });
//   };

//   const formatDate = (dateString: string | null) => {
//     if (!dateString) return '-';
//     try {
//       return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
//     } catch { return dateString; }
//   };

//   const handleBack = () => navigate('/teachers');

//   if (loading) {
//     return (
//       <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: '#f5f5f5', borderRadius: '12px' }}>
//         <div style={{ textAlign: 'center' }}>
//           <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
//           <p style={{ marginTop: '16px', color: '#0f3d3e', fontSize: '0.95rem' }}>Loading teacher details...</p>
//         </div>
//       </div>
//     );
//   }

//   if (error || !teacher) {
//     return (
//       <div>
//         <Message severity="error" text={error || 'Teacher not found'} style={{ marginBottom: '20px' }} />
//         <Button label="Back to Teachers" icon="pi pi-arrow-left" onClick={handleBack} outlined style={{ color: '#0f3d3e', borderColor: '#0f3d3e' }} />
//       </div>
//     );
//   }

//   return (
//     <div style={{ padding: '0' }}>
//       <Toast ref={toast} />

//       {/* ── Reset Password Modal ──────────────────────────────────────────── */}
//       <ResetPasswordModal
//         visible={showResetModal}
//         onHide={() => setShowResetModal(false)}
//         onSuccess={() => {
//           setShowResetModal(false);
//           toast.current?.show({
//             severity: 'success',
//             summary: 'Password Reset',
//             detail: `Password for ${teacher.name} has been reset successfully`,
//             life: 4000,
//           });
//         }}
//         profileId={teacher.teacher_id}
//         role="teacher"
//         name={teacher.name}
//       />

//       {/* ── Hero Banner ───────────────────────────────────────────────────── */}
//       <div style={{
//         background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//         borderRadius: '20px', padding: '32px', marginBottom: '24px',
//         color: 'white', position: 'relative', overflow: 'hidden',
//         boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)',
//       }}>
//         <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
//         <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '250px', height: '250px', background: 'rgba(212, 241, 126, 0.08)', borderRadius: '50%', filter: 'blur(50px)' }} />

//         <div style={{ position: 'relative', zIndex: 1 }}>
//           <button
//             onClick={handleBack}
//             style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '20px', transition: 'all 0.2s ease' }}
//             onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
//             onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
//           >
//             <i className="pi pi-arrow-left" /> Back to Teachers
//           </button>

//           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>
//             <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1, minWidth: '250px' }}>
//               <Avatar
//                 image={teacher.profilePicture?.cdn_url || undefined}
//                 icon={!teacher.profilePicture ? 'pi pi-user' : undefined}
//                 size="xlarge"
//                 shape="circle"
//                 style={{ width: '100px', height: '100px', backgroundColor: teacher.profilePicture ? 'transparent' : 'rgba(255,255,255,0.2)', border: '4px solid rgba(212,241,126,0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
//               />
//               <div>
//                 <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212,241,126,0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
//                   Teacher Profile
//                 </div>
//                 <h1 style={{ margin: '0 0 8px 0', fontSize: '1.7rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>{teacher.name}</h1>
//                 <p style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>Employee ID: {teacher.employeeId || 'N/A'}</p>
//                 <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
//                   {teacher.mobileNumber && (
//                     <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '10px 14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', gap: '8px' }}>
//                       <i className="pi pi-phone" style={{ fontSize: '0.85rem', color: '#d4f17e' }} />
//                       <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: 'white' }}>{teacher.mobileNumber}</p>
//                     </div>
//                   )}
//                   {teacher.email && (
//                     <div style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '10px 14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', gap: '8px' }}>
//                       <i className="pi pi-envelope" style={{ fontSize: '0.85rem', color: '#d4f17e' }} />
//                       <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: 'white' }}>{teacher.email}</p>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>

//             <div style={{ textAlign: 'right' }}>
//               <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
//                 <Tag value={teacher.status.toUpperCase()} severity={teacher.status === 'active' ? 'success' : 'danger'} style={{ fontSize: '0.7rem', padding: '6px 12px', fontWeight: '700' }} />
//                 <Tag value={teacher.is_registered ? 'REGISTERED' : 'NOT REGISTERED'} severity={teacher.is_registered ? 'success' : 'warning'} icon={teacher.is_registered ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'} style={{ fontSize: '0.7rem', padding: '6px 12px', fontWeight: '700' }} />
//               </div>
//               <p style={{ margin: '0 0 8px 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>Date of Birth: {formatDate(teacher.dateOfBirth)}</p>
//               <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>Joined {formatDate(teacher.dateOfJoining)}</p>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* ── Main Grid ─────────────────────────────────────────────────────── */}
//       <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '24px' }}>

//         {/* Left Column */}
//         <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

//           {/* Not-registered warning */}
//           {!teacher.is_registered && (
//             <div style={{ background: '#fff3cd', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '2px solid #ffc107' }}>
//               <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
//                   <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem', color: '#856404' }} />
//                   <div>
//                     <p style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: '700', color: '#856404' }}>Account Not Registered</p>
//                     <p style={{ margin: 0, fontSize: '0.85rem', color: '#856404' }}>Generate login credentials to allow teacher portal access. A welcome email will be sent automatically.</p>
//                   </div>
//                 </div>
//                 <Button label="Generate Credentials" icon="pi pi-key" loading={generatingCredentials} onClick={handleGenerateCredentials} style={{ background: '#856404', border: 'none', padding: '10px 20px', fontSize: '0.85rem', fontWeight: '600', whiteSpace: 'nowrap' }} />
//               </div>
//             </div>
//           )}

//           <InfoSection title="Personal Information" icon="pi-user" fields={[
//             { label: 'Gender', value: teacher.gender ? teacher.gender.charAt(0).toUpperCase() + teacher.gender.slice(1) : '-' },
//             { label: 'Blood Group', value: teacher.bloodGroup || '-' },
//             { label: 'Category', value: teacher.category || '-' },
//             { label: 'Aadhaar Number', value: teacher.aadharNumber || '-' },
//             { label: 'Marital Status', value: teacher.maritalStatus ? teacher.maritalStatus.charAt(0).toUpperCase() + teacher.maritalStatus.slice(1) : '-' },
//           ]} />

//           <InfoSection title="Professional Information" icon="pi-briefcase" fields={[
//             { label: 'Qualification', value: teacher.qualification || '-' },
//             { label: 'Experience', value: teacher.experience || '-' },
//             { label: 'Employee ID', value: teacher.employeeId || '-' },
//             { label: 'Date of Joining', value: formatDate(teacher.dateOfJoining) },
//           ]} />

//           <InfoSection title="Contact Information" icon="pi-phone" fields={[
//             { label: 'Mobile Number', value: teacher.mobileNumber || '-' },
//             { label: 'WhatsApp Number', value: teacher.whatsappNumber || '-' },
//             { label: 'Email', value: teacher.email || '-' },
//           ]} />

//           {/* Address */}
//           <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
//             <SectionHeader title="Address" subtitle="Residential information" icon="pi-map-marker" />
//             <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '10px', marginBottom: '16px' }}>
//               <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Full Address</p>
//               <p style={{ margin: 0, fontSize: '0.95rem', color: '#333', lineHeight: '1.5', fontWeight: '500' }}>
//                 {[teacher.wardNo && `Ward ${teacher.wardNo}`, teacher.villageTown, teacher.tehsil, teacher.district, teacher.state].filter(Boolean).join(', ') || '-'}
//               </p>
//               {teacher.pinCode && <p style={{ margin: '6px 0 0 0', fontSize: '0.9rem', fontWeight: '700', color: '#0f3d3e' }}>Pincode: {teacher.pinCode}</p>}
//             </div>
//             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
//               <InfoField label="Ward No" value={teacher.wardNo || '-'} />
//               <InfoField label="Village/Town" value={teacher.villageTown || '-'} />
//               <InfoField label="Tehsil" value={teacher.tehsil || '-'} />
//             </div>
//           </div>

//           {/* Family / Emergency */}
//           {(teacher.spouseName || teacher.spouseContact || teacher.emergencyContactName || teacher.emergencyContactNumber) && (
//             <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
//               <SectionHeader title="Family & Emergency Contact" subtitle="Personal contacts" icon="pi-users" />
//               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
//                 {(teacher.spouseName || teacher.spouseContact) && (
//                   <div style={{ background: '#f8f9fa', padding: '14px', borderRadius: '10px' }}>
//                     <p style={{ margin: '0 0 10px 0', fontSize: '0.7rem', color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Spouse Details</p>
//                     <InfoField label="Name" value={teacher.spouseName || '-'} compact />
//                     <InfoField label="Contact" value={teacher.spouseContact || '-'} compact />
//                   </div>
//                 )}
//                 {(teacher.emergencyContactName || teacher.emergencyContactNumber) && (
//                   <div style={{ background: '#f8f9fa', padding: '14px', borderRadius: '10px' }}>
//                     <p style={{ margin: '0 0 10px 0', fontSize: '0.7rem', color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Emergency Contact</p>
//                     <InfoField label="Name" value={teacher.emergencyContactName || '-'} compact />
//                     <InfoField label="Number" value={teacher.emergencyContactNumber || '-'} compact />
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </div>

//         {/* Right Column — Quick Info */}
//         <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
//           <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
//             <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: '700', color: '#0f3d3e' }}>Quick Info</h3>

//             <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)', borderRadius: '12px', marginBottom: '16px' }}>
//               <Avatar
//                 image={teacher.profilePicture?.cdn_url || undefined}
//                 icon={!teacher.profilePicture ? 'pi pi-user' : undefined}
//                 size="xlarge"
//                 style={{ width: '80px', height: '80px', backgroundColor: teacher.profilePicture ? 'transparent' : '#d4f17e', color: '#0f3d3e', fontSize: '2rem', fontWeight: '700', marginBottom: '12px', border: '3px solid rgba(212,241,126,0.3)' }}
//                 shape="circle"
//               />
//               <p style={{ margin: '0 0 6px 0', fontSize: '1.1rem', fontWeight: '700', color: 'white', textAlign: 'center' }}>{teacher.name}</p>
//               <p style={{ margin: '0 0 10px 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>{teacher.qualification || 'Teacher'}</p>
//               <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
//                 <Tag value={teacher.status.toUpperCase()} severity={teacher.status === 'active' ? 'success' : 'danger'} style={{ fontSize: '0.7rem', padding: '5px 10px', fontWeight: '700' }} />
//                 <Tag value={teacher.is_registered ? 'REGISTERED' : 'NOT REGISTERED'} severity={teacher.is_registered ? 'success' : 'warning'} style={{ fontSize: '0.7rem', padding: '5px 10px', fontWeight: '700' }} />
//               </div>
//             </div>

//             <QuickInfoRow label="Teacher ID" value={teacher.teacher_id} mono />
//             <QuickInfoRow label="Employee ID" value={teacher.employeeId || '-'} />
//             <QuickInfoRow label="Date of Birth" value={formatDate(teacher.dateOfBirth)} />
//             {teacher.gender && <QuickInfoRow label="Gender" value={teacher.gender.charAt(0).toUpperCase() + teacher.gender.slice(1)} />}
//             {teacher.category && <QuickInfoRow label="Category" value={teacher.category} />}
//           </div>

//           {/* ── Reset Password Card (only if registered) ──────────────────── */}
//           {teacher.is_registered && (
//             <div style={{
//               background: 'white', borderRadius: '16px', padding: '20px',
//               boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
//             }}>
//               <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
//                 <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #0f3d3e, #1a5555)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                   <i className="pi pi-lock" style={{ color: '#d4f17e', fontSize: '0.9rem' }} />
//                 </div>
//                 <div>
//                   <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#0f3d3e' }}>Password Management</p>
//                   <p style={{ margin: 0, fontSize: '0.72rem', color: '#999' }}>Operator controls</p>
//                 </div>
//               </div>
//               <p style={{ margin: '0 0 14px 0', fontSize: '0.78rem', color: '#6b7280', lineHeight: '1.5' }}>
//                 Reset this teacher's login password if they've forgotten it or need access restored.
//               </p>
//               <button
//                 onClick={() => setShowResetModal(true)}
//                 style={{
//                   width: '100%', padding: '10px 16px',
//                   background: 'linear-gradient(135deg, #0f3d3e, #1a5555)',
//                   border: 'none', borderRadius: '10px',
//                   color: 'white', fontSize: '0.82rem', fontWeight: '700',
//                   cursor: 'pointer', display: 'flex', alignItems: 'center',
//                   justifyContent: 'center', gap: '8px', transition: 'opacity 0.2s',
//                 }}
//                 onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
//                 onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
//               >
//                 <i className="pi pi-refresh" style={{ fontSize: '0.82rem' }} />
//                 Reset Password
//               </button>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* ── Action Buttons ─────────────────────────────────────────────────── */}
//       <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
//         <Button label="Edit Teacher" icon="pi pi-pencil" onClick={() => navigate(`/teachers/edit/${teacherId}`)} style={{ background: '#0f3d3e', border: 'none', padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600' }} />
//         {!teacher.is_registered && (
//           <Button label="Generate Credentials" icon="pi pi-key" loading={generatingCredentials} onClick={handleGenerateCredentials} outlined style={{ color: '#856404', borderColor: '#856404', padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600' }} />
//         )}
//         {teacher.is_registered && (
//           <Button
//             label="Reset Password"
//             icon="pi pi-lock"
//             onClick={() => setShowResetModal(true)}
//             outlined
//             style={{ color: '#0f3d3e', borderColor: '#0f3d3e', padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600' }}
//           />
//         )}
//         <Button label="Assign Batches" icon="pi pi-users" outlined style={{ color: '#0f3d3e', borderColor: '#0f3d3e', padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600' }} />
//         <Button label="View Attendance" icon="pi pi-calendar" outlined style={{ color: '#0f3d3e', borderColor: '#0f3d3e', padding: '12px 24px', fontSize: '0.85rem', fontWeight: '600' }} />
//       </div>

//       {/* ── Credentials Dialog (unchanged) ─────────────────────────────────── */}
//       <Dialog
//         header={
//           <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
//             <i className="pi pi-check-circle" style={{ fontSize: '1.5rem', color: '#10b981' }} />
//             <div>
//               <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f3d3e' }}>Teacher Credentials Generated</h3>
//               <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#666' }}>{emailSent ? 'Welcome email sent successfully' : 'Please share credentials manually'}</p>
//             </div>
//           </div>
//         }
//         visible={showCredentialsDialog}
//         style={{ width: '550px' }}
//         onHide={() => { setShowCredentialsDialog(false); setCredentials(null); setEmailSent(false); }}
//         footer={
//           <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
//             <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
//               {emailSent
//                 ? <><i className="pi pi-check-circle" style={{ color: '#10b981', fontSize: '1rem' }} /><span style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: '600' }}>Email sent to teacher</span></>
//                 : <><i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b', fontSize: '1rem' }} /><span style={{ fontSize: '0.85rem', color: '#f59e0b', fontWeight: '600' }}>Email not sent</span></>}
//             </div>
//             <div style={{ display: 'flex', gap: '12px' }}>
//               <Button label="Copy All" icon="pi pi-copy" onClick={copyAllCredentials} outlined style={{ color: '#0f3d3e', borderColor: '#0f3d3e', fontSize: '0.85rem', fontWeight: '600' }} />
//               <Button label="Close" icon="pi pi-times" onClick={() => { setShowCredentialsDialog(false); setCredentials(null); setEmailSent(false); }} style={{ background: '#0f3d3e', border: 'none', fontSize: '0.85rem', fontWeight: '600' }} />
//             </div>
//           </div>
//         }
//       >
//         {credentials && (
//           <div style={{ padding: '20px 0' }}>
//             <div style={{ background: emailSent ? 'linear-gradient(135deg,#d1fae5,#a7f3d0)' : 'linear-gradient(135deg,#fef3c7,#fde68a)', padding: '16px', borderRadius: '12px', marginBottom: '20px', border: emailSent ? '1px solid #10b981' : '1px solid #f59e0b' }}>
//               <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
//                 <i className={emailSent ? 'pi pi-check-circle' : 'pi pi-info-circle'} style={{ fontSize: '1.2rem', color: emailSent ? '#065f46' : '#92400e', marginTop: '2px' }} />
//                 <div style={{ flex: 1 }}>
//                   <p style={{ margin: '0 0 6px 0', fontSize: '0.9rem', fontWeight: '700', color: emailSent ? '#065f46' : '#92400e' }}>{emailSent ? '✨ Welcome Email Sent Successfully' : '⚠️ Manual Sharing Required'}</p>
//                   <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: '1.5', color: emailSent ? '#047857' : '#78350f' }}>
//                     {emailSent ? `A welcome email with login credentials has been sent to ${credentials.email}.` : 'The welcome email could not be sent. Please share credentials manually.'}
//                   </p>
//                 </div>
//               </div>
//             </div>

//             {(credentials.institute_name || credentials.branch_name) && (
//               <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
//                 <p style={{ margin: '0 0 12px 0', fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teacher Details</p>
//                 {credentials.institute_name && <div style={{ marginBottom: '8px' }}><span style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '600' }}>Institute: </span><span style={{ fontSize: '0.85rem', color: '#0f172a', fontWeight: '600' }}>{credentials.institute_name}</span></div>}
//                 {credentials.branch_name && <div><span style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '600' }}>Branch: </span><span style={{ fontSize: '0.85rem', color: '#0f172a', fontWeight: '600' }}>{credentials.branch_name}</span></div>}
//               </div>
//             )}

//             <CredentialRow label="Email / Username" value={credentials.email} onCopy={() => copyToClipboard(credentials.email, 'Email')} />
//             <CredentialRow label="Temporary Password" value={credentials.temporary_password} onCopy={() => copyToClipboard(credentials.temporary_password, 'Password')} highlight />

//             <div style={{ background: 'linear-gradient(135deg,#fef3c7,#fde68a)', border: '1px solid #fbbf24', borderRadius: '12px', padding: '16px', marginTop: '20px' }}>
//               <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
//                 <i className="pi pi-shield" style={{ fontSize: '1.1rem', color: '#92400e', marginTop: '2px' }} />
//                 <div>
//                   <p style={{ margin: '0 0 6px 0', fontSize: '0.85rem', fontWeight: '700', color: '#92400e' }}>Important Security Notice</p>
//                   <p style={{ margin: 0, fontSize: '0.8rem', color: '#78350f', lineHeight: '1.5' }}>Teacher must change this temporary password immediately after first login.</p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}
//       </Dialog>
//     </div>
//   );
// };

// // ── Sub-components ─────────────────────────────────────────────────────────────

// const SectionHeader: React.FC<{ title: string; subtitle: string; icon: string }> = ({ title, subtitle, icon }) => (
//   <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
//     <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg,#0f3d3e,#1a5555)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//       <i className={`pi ${icon}`} style={{ color: '#d4f17e', fontSize: '1.1rem' }} />
//     </div>
//     <div>
//       <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>{title}</h2>
//       <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: '#999' }}>{subtitle}</p>
//     </div>
//   </div>
// );

// interface InfoSectionProps {
//   title: string;
//   icon: string;
//   fields: Array<{ label: string; value: string | null }>;
// }
// const InfoSection: React.FC<InfoSectionProps> = ({ title, icon, fields }) => (
//   <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
//     <SectionHeader title={title} subtitle={`Teacher ${title.toLowerCase()}`} icon={icon} />
//     <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '16px' }}>
//       {fields.map((f, i) => <InfoField key={i} label={f.label} value={f.value} />)}
//     </div>
//   </div>
// );

// const InfoField: React.FC<{ label: string; value: string | null; compact?: boolean }> = ({ label, value, compact }) => (
//   <div style={{ marginBottom: compact ? '8px' : '0' }}>
//     <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>{label}</p>
//     <p style={{ margin: 0, fontSize: compact ? '0.8rem' : '0.9rem', fontWeight: '600', color: '#333' }}>{value || '-'}</p>
//   </div>
// );

// const QuickInfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
//   <div style={{ marginBottom: '12px' }}>
//     <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>{label}</p>
//     <p style={{ margin: 0, fontSize: mono ? '0.8rem' : '0.85rem', fontWeight: '600', color: mono ? '#0f3d3e' : '#333', fontFamily: mono ? 'monospace' : undefined, background: mono ? '#f8f9fa' : undefined, padding: mono ? '8px 10px' : undefined, borderRadius: mono ? '8px' : undefined, wordBreak: 'break-all' }}>{value}</p>
//   </div>
// );

// const CredentialRow: React.FC<{ label: string; value: string; onCopy: () => void; highlight?: boolean }> = ({ label, value, onCopy, highlight }) => (
//   <div style={{ marginBottom: '16px' }}>
//     <p style={{ margin: '0 0 8px 0', fontSize: '0.75rem', color: '#999', fontWeight: '600', textTransform: 'uppercase' }}>{label}</p>
//     <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: highlight ? '#fff3cd' : '#f8f9fa', padding: '12px', borderRadius: '8px', border: highlight ? '1px solid #ffc107' : '1px solid #e9ecef' }}>
//       <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: highlight ? '700' : '600', color: highlight ? '#856404' : '#0f3d3e', flex: 1, fontFamily: 'monospace', letterSpacing: highlight ? '1px' : undefined }}>{value}</p>
//       <button onClick={onCopy} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '6px', color: highlight ? '#856404' : '#0f3d3e' }} title="Copy">
//         <i className="pi pi-copy" />
//       </button>
//     </div>
//   </div>
// );

// export default TeacherDetails;